import { isDefaultBidderRuleFor } from '@pubstack/common/src/bidder-rule'
import { CreateStack, Stack } from '@pubstack/common/src/stack'
import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useBidderRules } from '~/api/bidder-rules.api'
import { useCreateStack } from '~/api/stacks.api'
import { Title_P1 } from '~/components/_styled/titles'
import { useScopePermissions } from '~/auth/useScopePermissions'
import Button from '~/components/Button'
import { Tooltip } from '~/components/Tooltip'
import { Flyout } from '~/components/Flyout'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Spinner } from '~/components/Spinner'
import { useToast } from '~/components/Toast/useToasts'
import { FloorPriceSelection } from './FloorPriceSelection'

const SECTION = 'mb-4 flex flex-col gap-3 py-2 font-roboto text-base/[26px]'

// Tracking labels
const SAVE_STACK_TRACKING_LABEL = 'pbs-update-stack-save'
const SELECT_BIDDER_RULE_TRACKING_LABEL = 'pbs-update-stack-select-bidder-rule'

type Props = {
  siteId: string
  stack: Stack
  onClose: () => void
}

export const EditStackFlyout: FunctionComponent<Props> = ({ siteId, stack, onClose }) => {
  // Saving a stack is owner-only at the API. The form still opens and reads, which is what the flyout is
  // worth to anyone who cannot save it.
  const { canEdit, disabledReason } = useScopePermissions()
  // States
  const initialFloorToggle = !!stack.configuration.floorRuleConfig
  const [isFloorToggleChecked, setIsFloorToggleChecked] = useState<boolean>(initialFloorToggle)
  // API Hooks
  const { data: bidderRules, isLoading: isBidderRulesLoading } = useBidderRules('pbs')
  const { mutateAsync: createStack, isPending: isStackCreationPending } = useCreateStack()

  // Hooks
  const form = useForm<CreateStack>({
    defaultValues: {
      configuration: {
        bidderRuleId: stack?.configuration.bidderRuleId || '',
        // Re-opening the flyout has to show the floor rules the stack was saved with. `specificFloorRuleConfigs`
        // is stored as null when there is none, which the field array cannot iterate.
        floorRuleConfig: stack.configuration.floorRuleConfig
          ? { ...stack.configuration.floorRuleConfig, specificFloorRuleConfigs: stack.configuration.floorRuleConfig.specificFloorRuleConfigs ?? [] }
          : undefined,
      },
    },
    mode: 'onChange',
  })
  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
    setValue,
  } = form
  const toast = useToast()

  const bidderRuleOptions: SelectOptionProp[] = bidderRules.map((rule) => ({
    value: rule.id,
    label: rule.name,
    trackingLabel: SELECT_BIDDER_RULE_TRACKING_LABEL as TrackingLabel,
  }))

  const onSubmit = useCallback(
    async (e: CreateStack) => {
      if (isStackCreationPending) return

      try {
        const newStack: CreateStack = {
          type: 'pbs',
          configuration: { contextId: null, bidderRuleId: e.configuration.bidderRuleId, floorRuleConfig: isFloorToggleChecked ? e.configuration.floorRuleConfig : undefined },
          name: stack.name,
          siteId: stack.siteId!,
          stackGroupId: stack?.stackGroupId || '',
          priority: stack.priority,
        }
        // The backend auto-archives existing to-deploy stacks in the same group on create
        await createStack(newStack)

        // No navigation on save: the only opener is the stacks table, so the user is already where this led,
        // and `createStack` invalidates the list.
        onClose()
      } catch (error) {
        toast.alert(error.description.message)
      }
    },
    [createStack, isStackCreationPending, onClose, stack, toast, isFloorToggleChecked]
  )

  useEffect(() => {
    setValue('configuration.bidderRuleId', stack?.configuration.bidderRuleId || bidderRules.find((rule) => isDefaultBidderRuleFor(rule, 'pbs'))?.id || '')
  }, [bidderRules, setValue, stack?.configuration.bidderRuleId])

  const toggleChanged = isFloorToggleChecked !== initialFloorToggle

  return (
    <Flyout.Content>
      <Flyout.Title>Edit stack</Flyout.Title>
      {isBidderRulesLoading ? (
        <Spinner />
      ) : (
        <FormProvider {...form}>
          <Flyout.Body>
            <div className={SECTION}>
              <Title_P1>Header bidding</Title_P1>
              <Select control={control} name="configuration.bidderRuleId" label="Bidder rule" required options={bidderRuleOptions} />
              <div className={'font-roboto text-sm/[22px] text-slate-grey italic'}>User sync is automatically enabled.</div>
            </div>
            <div className={SECTION}>
              <Title_P1>Floor price</Title_P1>
              <FloorPriceSelection stack={stack} siteId={siteId} isToggleChecked={isFloorToggleChecked} setIsToggleChecked={setIsFloorToggleChecked} />
            </div>
          </Flyout.Body>
          <Flyout.Actions>
            <Tooltip title={disabledReason} disabled={canEdit}>
              <Button
                disabled={!canEdit || (!isDirty && !toggleChanged) || (isFloorToggleChecked && !isValid) || isStackCreationPending}
                iconName={isStackCreationPending ? 'pubstack' : undefined}
                iconSpin={isStackCreationPending}
                onClick={handleSubmit(onSubmit)}
                trackingLabel={SAVE_STACK_TRACKING_LABEL}
              >
                {isStackCreationPending ? 'Saving...' : 'Save'}
              </Button>
            </Tooltip>
          </Flyout.Actions>
        </FormProvider>
      )}
    </Flyout.Content>
  )
}
